import { useMediaQuery } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { TextfieldPopper } from 'components/controls/misc'
import { GenericContentPopper } from 'components/controls/misc/GenericContentPopper'
import { Button, CustomTooltip } from 'components/facets'
import { useOrderStore } from 'lib/hooks'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getOrderState, hasIssues, hasUnresolvedIssues, Order, ORDER_SIDEBAR_TABS } from 'shared'
import { ArticlesTable } from './ArticlesTable'
import { OrderInfo } from './OrderInfo'

interface OrderDetailsDialogProps {
  open: boolean
  onClose: () => void
  order: Order
  handleNextOrderDetail: () => void
  handlePrevOrderDetail: () => void
  handleConfirmReportIssueDialog: (issue: string, issueCode: string) => Promise<void>
  handleConfirmSolveIssueDialog: (solution: string) => Promise<void>
  handleChangeOrderState: (orderId: number, newState: string) => void
  handleConfirmTrackingNumberDialog: (trackingNumber: string) => Promise<void>
  viewOnly?: boolean
}

export const OrderDetailsDialog = ({
  open,
  onClose,
  order,
  handleNextOrderDetail,
  handlePrevOrderDetail,
  handleConfirmReportIssueDialog,
  handleConfirmSolveIssueDialog,
  handleChangeOrderState,
  handleConfirmTrackingNumberDialog,
  viewOnly,
}: OrderDetailsDialogProps): JSX.Element => {
  const { t } = useTranslation()
  const { activeTab } = useOrderStore()
  // On tablets and phones the dialog takes the whole screen and the arrows move to the header,
  // where they no longer eat the width the articles table needs
  const isCompact = useMediaQuery('(max-width: 991.98px)')
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [openIssueDetailPopper, setOpenIssueDetailPopper] = useState(false)
  const [popperType, setPopperType] = useState<
    null | 'tracking' | 'solveAllIssues' | 'issueDetail' | 'addOrderIssue'
  >(null)

  setOpenIssueDetailPopper

  const handleClose = () => {
    onClose()
  }

  const inspectOrderArticleIssue = () => {
    return
  }

  const prevArrow = (
    <CustomTooltip title={t('orders.previousOrder') as string}>
      <div
        className="directional-arrow me-2"
        onClick={handlePrevOrderDetail}
        data-testid="prev-order"
      >
        <i className="fa-regular fa-chevron-left"></i>
      </div>
    </CustomTooltip>
  )
  const nextArrow = (
    <CustomTooltip title={t('orders.nextOrder') as string}>
      <div
        className="directional-arrow ms-2"
        onClick={handleNextOrderDetail}
        data-testid="next-order"
      >
        <i className="fa-regular fa-chevron-right"></i>
      </div>
    </CustomTooltip>
  )

  return (
    <ClosableDialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={isCompact}
      maxWidth="xl"
      onKeyDown={(e) => {
        // Arrows keep moving the cursor while typing or while a popper (tracking, issues) is open
        if (anchorEl || (e.target as HTMLElement).closest('input, textarea')) return
        if (e.key === 'ArrowLeft') handlePrevOrderDetail()
        else if (e.key === 'ArrowRight') handleNextOrderDetail()
      }}
    >
      <div className="d-flex align-items-end justify-content-between mb-4 order-details-dialog">
        <div>
          <h3 className="mb-1 ">
            {t('orders.order')}: {order?.sourceOrderId}
          </h3>
          <h6 className="m-0 p-0 fw-normal">
            {t('common.channel')}: {order.source}
          </h6>
          <h6 className="m-0 p-0 fw-normal">
            {t('orders.status')}: {getOrderState(order)}
          </h6>
        </div>
        {isCompact && (
          <div className="d-flex">
            {prevArrow}
            {nextArrow}
          </div>
        )}
      </div>
      <div className="d-flex flex-row w-100 align-items-center order-details-dialog">
        {!isCompact && prevArrow}

        <div className="w-100" style={{ minWidth: 0 }}>
          <div className="mb-4">
            <OrderInfo order={order} />
          </div>

          <ArticlesTable
            key={order?.ptOrderId}
            articles={order?.articles || []}
            setOpenReportIssueDialog={inspectOrderArticleIssue}
            parentPage="dialog"
          />
          <div className="w-100 my-4 d-flex flex-wrap gap-2 justify-content-between">
            <div className={`d-flex flex-row flex-wrap gap-2 ${viewOnly ? 'd-none' : ''}`}>
              {(activeTab === ORDER_SIDEBAR_TABS.FULFILMENT_CENTER ||
                activeTab === ORDER_SIDEBAR_TABS.MANAGE_PICKING_LISTS ||
                activeTab === ORDER_SIDEBAR_TABS.PACKAGE) &&
                !hasIssues(order) &&
                !order.isCtZero && (
                  <div className="me-2">
                    <Button
                      data-testid="add-order-issue-dialog"
                      variant="outlined"
                      onClick={(e) => {
                        setPopperType('addOrderIssue')
                        setAnchorEl(e.currentTarget)
                      }}
                    >
                      {t('orders.addOrderIssue')}
                    </Button>
                  </div>
                )}
              {activeTab === ORDER_SIDEBAR_TABS.FULFILMENT_CENTER && (
                <Button variant="outlined">{t('orders.messageBuyer')}</Button>
              )}

              {activeTab === ORDER_SIDEBAR_TABS.PACKAGE && (
                <div className="d-flex ">
                  <Button
                    data-testid="add-tracking"
                    variant="outlined"
                    onClick={(e) => {
                      setPopperType('tracking')
                      setAnchorEl(e.currentTarget)
                    }}
                  >
                    Tracking
                  </Button>
                  <div className="mx-2">
                    {order.state.state === 'picked' && (
                      <Button
                        data-testid="mark-as-packed"
                        variant="outlined"
                        onClick={() => handleChangeOrderState(order.ptOrderId, 'packed')}
                      >
                        {t('orders.markAsPacked')}
                      </Button>
                    )}
                    {order.state.state === 'packed' && (
                      <Button
                        data-testid="mark-as-sent"
                        onClick={() => handleChangeOrderState(order.ptOrderId, 'sent')}
                        variant="outlined"
                      >
                        {t('orders.markAsSent')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {(activeTab === ORDER_SIDEBAR_TABS.PACKAGE ||
                activeTab === ORDER_SIDEBAR_TABS.MANAGE_PICKING_LISTS) &&
                hasUnresolvedIssues(order) &&
                !order.isCtZero && (
                  <Button
                    data-testid="solve-all-issues"
                    variant="outlined"
                    onClick={(e) => {
                      setPopperType('solveAllIssues')
                      setAnchorEl(e.currentTarget)
                    }}
                  >
                    Solve all issues
                  </Button>
                )}
            </div>
            <div>
              <Button variant="contained" data-testid="close" onClick={handleClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
        {!isCompact && nextArrow}

        {anchorEl && (
          <>
            {/* Overlay to prevent interaction with the rest of the app */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                // zIndex: 999,
              }}
              onClick={() => setAnchorEl(null)} // close popover
            />

            {popperType &&
              (popperType === 'issueDetail' ? (
                <GenericContentPopper
                  open={openIssueDetailPopper}
                  anchorEl={anchorEl}
                  setAnchorEl={setAnchorEl}
                  style={{ zIndex: 2000 }}
                >
                  <div>Issue detail</div>
                </GenericContentPopper>
              ) : (
                <TextfieldPopper
                  style={{ zIndex: 2000 }}
                  placeholder={
                    {
                      tracking: 'eg: 1SBD5S12S5VBVVDSWRFB',
                      solveAllIssues: 'eg: found them all',
                      addOrderIssue: 'eg: card not fount',
                    }[popperType]
                  }
                  name={popperType}
                  anchorEl={anchorEl}
                  setAnchorEl={setAnchorEl}
                  onSubmit={(inputValue) => {
                    if (popperType === 'tracking') {
                      handleConfirmTrackingNumberDialog(inputValue)
                      setAnchorEl(null)
                    } else if (popperType === 'solveAllIssues') {
                      handleConfirmSolveIssueDialog(inputValue)
                      setAnchorEl(null)
                    } else if (popperType === 'addOrderIssue') {
                      handleConfirmReportIssueDialog(inputValue, '')
                      setAnchorEl(null)
                    }
                  }}
                />
              ))}
          </>
        )}
      </div>
    </ClosableDialog>
  )
}
